import { useEffect, useState } from 'react';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  useExpenseCategories,
  useExtras,
  useLengths,
  useMasters,
  useServices,
  useTags,
} from '@/lib/queries';
import { useAuthStore } from '@/store/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { fmtMoney } from '@/lib/format';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useBackButton } from '@/lib/tg-buttons';

const Header = ({ title }: { title: string }) => {
  const nav = useNavigate();
  useBackButton(() => nav(-1));
  return (
    <div className="mb-3 flex items-center gap-2">
      <Button variant="ghost" size="icon" onClick={() => nav(-1)}>
        <ArrowLeft className="h-5 w-5" />
      </Button>
      <h1 className="text-xl font-bold">{title}</h1>
    </div>
  );
};

// ===== Services =====
export const ServicesSettings = () => {
  const { data: services = [] } = useServices();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: 'extension',
    name: '',
    color: '#8b5cf6',
    default_duration_min: 240,
    default_price: 0,
    needs_strands: true,
  });
  const submit = async () => {
    await api.post('/settings/services', form);
    qc.invalidateQueries({ queryKey: ['services'] });
    setOpen(false);
  };
  return (
    <div className="pb-12">
      <Header title="Услуги" />
      <div className="space-y-2">
        {services.map((s: any) => (
          <Card key={s.id}>
            <CardContent className="flex items-center justify-between p-3">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ background: s.color }} />
                <div>
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-muted-foreground">{s.default_duration_min} мин · {fmtMoney(Number(s.default_price))}</div>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={async () => { await api.delete(`/settings/services/${s.id}`); qc.invalidateQueries({ queryKey: ['services'] }); }}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="mt-3 w-full"><Plus className="h-4 w-4" /> Добавить</Button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>Новая услуга</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div><Label>Код</Label>
              <Select value={form.code} onValueChange={(v) => setForm({ ...form, code: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="extension">Наращивание</SelectItem>
                  <SelectItem value="correction">Коррекция</SelectItem>
                  <SelectItem value="removal">Снятие</SelectItem>
                  <SelectItem value="consultation">Консультация</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Название</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Цвет</Label><Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
            <div><Label>Длительность по умолчанию (мин)</Label><Input type="number" value={form.default_duration_min} onChange={(e) => setForm({ ...form, default_duration_min: Number(e.target.value) })} /></div>
            <div><Label>Цена по умолчанию</Label><Input type="number" value={form.default_price} onChange={(e) => setForm({ ...form, default_price: Number(e.target.value) })} /></div>
            <div className="flex items-center justify-between"><Label>Требует прядей</Label><Switch checked={form.needs_strands} onCheckedChange={(v) => setForm({ ...form, needs_strands: v })} /></div>
            <Button className="w-full" onClick={submit}>Создать</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ===== Extras =====
export const ExtrasSettings = () => {
  const { data: items = [] } = useExtras();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [price, setPrice] = useState<number>(0);
  const [dur, setDur] = useState<number>(0);
  const add = async () => {
    if (!name.trim()) return;
    await api.post('/settings/extras', { name, price, default_duration_min: dur });
    qc.invalidateQueries({ queryKey: ['extras'] });
    setName(''); setPrice(0); setDur(0);
  };
  return (
    <div className="pb-12">
      <Header title="Доп. услуги" />
      <Card>
        <CardContent className="grid grid-cols-3 gap-2 pt-4">
          <Input className="col-span-3" placeholder="Название" value={name} onChange={(e) => setName(e.target.value)} />
          <Input type="number" placeholder="Цена" value={price} onChange={(e) => setPrice(Number(e.target.value))} />
          <Input type="number" placeholder="Доп. время" value={dur} onChange={(e) => setDur(Number(e.target.value))} />
          <Button onClick={add}>Добавить</Button>
        </CardContent>
      </Card>
      <div className="mt-2 space-y-2">
        {items.map((e: any) => (
          <Card key={e.id}>
            <CardContent className="flex items-center justify-between p-3">
              <div>
                <div className="font-medium">{e.name}</div>
                <div className="text-xs text-muted-foreground">{fmtMoney(Number(e.price))} · +{e.default_duration_min} мин</div>
              </div>
              <Button variant="ghost" size="icon" onClick={async () => { await api.delete(`/settings/extras/${e.id}`); qc.invalidateQueries({ queryKey: ['extras'] }); }}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ===== Lengths =====
export const LengthsSettings = () => {
  const { data: lengths = [] } = useLengths();
  const qc = useQueryClient();
  const [val, setVal] = useState<number>(60);
  const add = async () => {
    await api.post('/settings/lengths', { length_cm: val });
    qc.invalidateQueries({ queryKey: ['lengths'] });
  };
  return (
    <div className="pb-12">
      <Header title="Длины волос" />
      <div className="flex gap-2">
        <Input type="number" value={val} onChange={(e) => setVal(Number(e.target.value))} />
        <Button onClick={add}>Добавить</Button>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {lengths.map((l: any) => (
          <Card key={l.id}>
            <CardContent className="flex items-center justify-between p-3">
              <span>{l.length_cm} см</span>
              <Button variant="ghost" size="icon" onClick={async () => { await api.delete(`/settings/lengths/${l.id}`); qc.invalidateQueries({ queryKey: ['lengths'] }); }}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ===== Tags =====
export const TagsSettings = () => {
  const { data: tags = [] } = useTags();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3b82f6');
  const add = async () => {
    if (!name.trim()) return;
    await api.post('/settings/tags', { name, color });
    qc.invalidateQueries({ queryKey: ['tags'] });
    setName('');
  };
  return (
    <div className="pb-12">
      <Header title="Теги клиентов" />
      <div className="flex gap-2">
        <Input placeholder="Название" value={name} onChange={(e) => setName(e.target.value)} />
        <Input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-16" />
        <Button onClick={add}>+</Button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {tags.map((t: any) => (
          <span key={t.id} className="flex items-center gap-1 rounded-full border px-3 py-1 text-sm" style={{ borderColor: t.color, color: t.color }}>
            {t.name}
            <button onClick={async () => { await api.delete(`/settings/tags/${t.id}`); qc.invalidateQueries({ queryKey: ['tags'] }); }}>
              <Trash2 className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
};

// ===== Expense Categories =====
export const CategoriesSettings = () => {
  const { data: cats = [] } = useExpenseCategories();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const add = async () => {
    if (!name.trim()) return;
    await api.post('/settings/expense-categories', { name });
    qc.invalidateQueries({ queryKey: ['expense_categories'] });
    setName('');
  };
  return (
    <div className="pb-12">
      <Header title="Категории расходов" />
      <div className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} />
        <Button onClick={add}>Добавить</Button>
      </div>
      <div className="mt-3 space-y-2">
        {cats.map((c: any) => (
          <Card key={c.id}>
            <CardContent className="flex items-center justify-between p-3">
              <span>{c.name}{c.is_system && <span className="ml-2 text-xs text-muted-foreground">(системная)</span>}</span>
              {!c.is_system && (
                <Button variant="ghost" size="icon" onClick={async () => { await api.delete(`/settings/expense-categories/${c.id}`); qc.invalidateQueries({ queryKey: ['expense_categories'] }); }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ===== Notifications =====
export const NotificationsSettings = () => {
  const isOwner = useAuthStore((s) => s.master?.role) === 'owner';
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .get('/settings/notifications')
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.error ?? e?.message ?? 'Ошибка загрузки');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div className="space-y-3 pb-12">
        <Header title="Уведомления" />
        <Card>
          <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
        </Card>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="space-y-3 pb-12">
        <Header title="Уведомления" />
        <div className="p-6 text-center text-muted-foreground">Загрузка…</div>
      </div>
    );
  }
  const update = (patch: any) => {
    const next = { ...data, ...patch };
    setData(next);
    api.put('/settings/notifications', next).catch((e) => {
      setError(e?.response?.data?.error ?? 'Не удалось сохранить настройки');
    });
  };
  return (
    <div className="space-y-2 pb-12">
      <Header title="Уведомления" />
      <Card><CardContent className="space-y-3 pt-4">
        {isOwner && (
          <>
            <Row label="Утренняя сводка" v={data.morning_summary_enabled} onChange={(v) => update({ morning_summary_enabled: v })} />
            <div className="flex items-center justify-between">
              <Label>Время утренней сводки</Label>
              <Input type="number" min={0} max={23} className="w-20" value={data.morning_summary_hour} onChange={(e) => update({ morning_summary_hour: Number(e.target.value) })} />
            </div>
          </>
        )}
        <Row label="За сутки до записи" v={data.day_before_enabled} onChange={(v) => update({ day_before_enabled: v })} />
        <Row label="За час до записи" v={data.hour_before_enabled} onChange={(v) => update({ hour_before_enabled: v })} />
        {isOwner && (
          <>
            <Row label="Итог дня вечером" v={data.evening_summary_enabled} onChange={(v) => update({ evening_summary_enabled: v })} />
            <Row label="Алерт по остаткам волос" v={data.stock_low_enabled} onChange={(v) => update({ stock_low_enabled: v })} />
            <div className="flex items-center justify-between">
              <Label>Порог низкого остатка (г)</Label>
              <Input type="number" className="w-24" value={data.stock_low_threshold_grams} onChange={(e) => update({ stock_low_threshold_grams: Number(e.target.value) })} />
            </div>
          </>
        )}
      </CardContent></Card>
    </div>
  );
};

const Row = ({ label, v, onChange }: { label: string; v: boolean; onChange: (v: boolean) => void }) => (
  <div className="flex items-center justify-between"><Label className="!text-foreground">{label}</Label><Switch checked={v} onCheckedChange={onChange} /></div>
);

// ===== Masters =====
export const MastersSettings = () => {
  const { data: masters = [] } = useMasters();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form, setForm] = useState({
    telegram_id: '',
    telegram_username: '',
    name: '',
    role: 'worker',
  });
  const create = async () => {
    await api.post('/settings/masters', form);
    qc.invalidateQueries({ queryKey: ['masters'] });
    setOpen(false);
    setForm({ telegram_id: '', telegram_username: '', name: '', role: 'worker' });
  };
  return (
    <div className="pb-12">
      <Header title="Работники" />
      <div className="space-y-2">
        {masters.map((m: any) => (
          <Card key={m.id}>
            <CardContent className="flex items-center justify-between p-3">
              <button
                className="flex-1 text-left"
                onClick={() => setEditing(m)}
              >
                <div className="font-medium">{m.name}</div>
                <div className="text-xs text-muted-foreground">
                  {m.role === 'owner' ? 'Владелец' : m.role === 'viewer' ? 'Только календарь' : 'Работник'}
                  {m.telegram_username ? ` · @${m.telegram_username}` : ''}
                  {m.telegram_id ? ` · TG ${m.telegram_id}` : ' · ожидает входа'}
                </div>
              </button>
              {m.role !== 'owner' && (
                <Button variant="ghost" size="icon" onClick={async () => { await api.delete(`/settings/masters/${m.id}`); qc.invalidateQueries({ queryKey: ['masters'] }); }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="mt-3 w-full"><Plus className="h-4 w-4" /> Добавить работника</Button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>Новый работник</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div><Label>Telegram @username</Label>
              <Input
                placeholder="@anna"
                value={form.telegram_username}
                onChange={(e) => setForm({ ...form, telegram_username: e.target.value })}
              />
            </div>
            <div className="text-center text-xs text-muted-foreground">или</div>
            <div><Label>Telegram ID</Label><Input value={form.telegram_id} onChange={(e) => setForm({ ...form, telegram_id: e.target.value })} /></div>
            <div><Label>Имя</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Роль</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="worker">Работник</SelectItem>
                  <SelectItem value="viewer">Только календарь</SelectItem>
                  <SelectItem value="owner">Владелец</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={create}
              disabled={!form.name || (!form.telegram_id && !form.telegram_username)}
              className="w-full"
            >
              Создать
            </Button>
            <div className="text-xs text-muted-foreground">
              При указании @username работник сможет войти из Telegram, и его ID привяжется автоматически.
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {editing && <EditMasterDialog master={editing} onClose={() => setEditing(null)} />}
    </div>
  );
};

const EditMasterDialog = ({ master, onClose }: { master: any; onClose: () => void }) => {
  const qc = useQueryClient();
  const [name, setName] = useState(master.name);
  const [phone, setPhone] = useState(master.phone ?? '');
  const save = async () => {
    await api.patch(`/settings/masters/${master.id}`, { name, phone: phone || '' });
    qc.invalidateQueries({ queryKey: ['masters'] });
    onClose();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Работник</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div><Label>Имя</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div><Label>Телефон</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
          <div className="text-xs text-muted-foreground">
            {master.telegram_id ? `Telegram ID: ${master.telegram_id}` : 'Telegram ID ещё не привязан'}
            {master.telegram_username ? ` · @${master.telegram_username}` : ''}
          </div>
          <Button onClick={save} className="w-full">Сохранить</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ===== Profile =====
const DAYS: Array<{ key: string; label: string }> = [
  { key: 'mon', label: 'Пн' },
  { key: 'tue', label: 'Вт' },
  { key: 'wed', label: 'Ср' },
  { key: 'thu', label: 'Чт' },
  { key: 'fri', label: 'Пт' },
  { key: 'sat', label: 'Сб' },
  { key: 'sun', label: 'Вс' },
];

export const ProfileSettings = () => {
  const { master } = useAuthStore();
  const [hours, setHours] = useState<Record<string, { from: string | null; to: string | null }>>(() => {
    const initial: any = {};
    DAYS.forEach((d) => {
      const v = (master?.working_hours as any)?.[d.key];
      initial[d.key] = { from: v?.from ?? '10:00', to: v?.to ?? '20:00' };
    });
    return initial;
  });
  const [enabled, setEnabled] = useState<Record<string, boolean>>(() => {
    const e: any = {};
    DAYS.forEach((d) => {
      const v = (master?.working_hours as any)?.[d.key];
      e[d.key] = v ? v.from !== null && v.to !== null : !['sat', 'sun'].includes(d.key);
    });
    return e;
  });
  const [saved, setSaved] = useState(false);

  if (!master) return null;

  const save = async () => {
    const out: any = {};
    DAYS.forEach((d) => {
      const cur = hours[d.key] ?? { from: null, to: null };
      out[d.key] = enabled[d.key]
        ? { from: cur.from, to: cur.to }
        : { from: null, to: null };
    });
    await api.patch(`/settings/masters/${master.id}`, { working_hours: out });
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  return (
    <div className="space-y-3 pb-12">
      <Header title="Профиль" />
      <Card>
        <CardContent className="space-y-2 pt-4">
          <div><Label>Имя</Label><div className="font-medium">{master.name}</div></div>
          {master.telegram_id && (
            <div><Label>Telegram ID</Label><div className="font-mono text-sm">{master.telegram_id}</div></div>
          )}
          <div><Label>Роль</Label><div className="font-medium">{master.role === 'owner' ? 'Владелец' : master.role === 'viewer' ? 'Только календарь' : 'Работник'}</div></div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-2 pt-4">
          <Label>Рабочие часы</Label>
          {DAYS.map((d) => (
            <div key={d.key} className="flex items-center gap-2">
              <Switch
                checked={enabled[d.key]}
                onCheckedChange={(v) => setEnabled({ ...enabled, [d.key]: v })}
              />
              <span className="w-8 text-sm font-medium">{d.label}</span>
              <Input
                type="time"
                disabled={!enabled[d.key]}
                value={hours[d.key]?.from ?? ''}
                onChange={(e) => {
                  const cur = hours[d.key] ?? { from: null, to: null };
                  setHours({ ...hours, [d.key]: { from: e.target.value, to: cur.to } });
                }}
                className="flex-1"
              />
              <span>—</span>
              <Input
                type="time"
                disabled={!enabled[d.key]}
                value={hours[d.key]?.to ?? ''}
                onChange={(e) => {
                  const cur = hours[d.key] ?? { from: null, to: null };
                  setHours({ ...hours, [d.key]: { from: cur.from, to: e.target.value } });
                }}
                className="flex-1"
              />
            </div>
          ))}
          <Button onClick={save} className="w-full">{saved ? 'Сохранено' : 'Сохранить рабочие часы'}</Button>
        </CardContent>
      </Card>
    </div>
  );
};
